import { pdkAxios } from '@/lib/pdk-axios.ts';
import { db } from '@/models/db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
} from '@/models/hydrate.ts';
import { ProtectionPlanName } from '@/models/registry.ts';
import type {
  BaseStore,
  StoreHydrationDelegator,
} from '@/models/stores/base.ts';
import type { ProtectionPlanName as ProtectionPlanNameType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class ProtectionPlanNameStore
  implements BaseStore<ProtectionPlanName, ProtectionPlanNameType>
{
  store: ObservableMap<string, ProtectionPlanName> = observable.map();

  constructor() {
    makeAutoObservable(this, { store: observable.shallow }, { autoBind: true });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ProtectionPlanNameType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanNameType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.protection_plan_names.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): ProtectionPlanName | undefined {
    return this.store.get(id);
  }

  add(input: ProtectionPlanNameType) {
    const protectionPlanName = new ProtectionPlanName(input);
    this.store.set(protectionPlanName.id, protectionPlanName);
  }

  save(protectionPlanName: ProtectionPlanName) {
    this.store.delete(protectionPlanName.id);
    this.store.set(protectionPlanName.id, protectionPlanName);
  }

  upsert(protectionPlanName: ProtectionPlanNameType) {
    // TODO -- temp fix, shouldnt be explicit casting id to string
    const idToUse = protectionPlanName.id;
    const existingProtectionPlanName = this.getById(idToUse);
    if (existingProtectionPlanName) {
      this.replace({ id: idToUse, protectionPlanName });
    } else {
      this.add(protectionPlanName);
    }
  }

  replace({
    id,
    protectionPlanName,
  }: {
    id: string;
    protectionPlanName: ProtectionPlanNameType;
  }) {
    const existingProtectionPlanName = this.getById(id);
    if (!existingProtectionPlanName) return;
    existingProtectionPlanName.name = protectionPlanName.name;

    this.save(existingProtectionPlanName);
  }
}

export const protectionPlanNameStore = new ProtectionPlanNameStore();

// TODO -- currently doing full hydrate every time, since protection plan names cant be tracked via conventional delta sync
class ProtectionPlanNameStoreHydrationDelegator
  implements StoreHydrationDelegator<ProtectionPlanNameType>
{
  async hydrate({
    fullHydrateEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string | null;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<ProtectionPlanNameType>>(
        fullHydrateEndpoint,
      );
    for (const protectionPlanName of resp.data.data) {
      await db.protection_plan_names.put(
        protectionPlanName,
        protectionPlanName.id,
      );
      protectionPlanNameStore.upsert(protectionPlanName);
    }
  }
}

export const protectionPlanNameStoreHydrationDelegator =
  new ProtectionPlanNameStoreHydrationDelegator();
